import { useState, useEffect } from 'react';
import {
  heroUnderwaterBluefin,
  heroBluefinDeck,
  heroChristosGrouper,
  heroWsbKelp,
  heroBluefinCloseup,
  captainChristosYellowtail,
  diverWsbUnderwater,
  boatWorldcatAerial,
  boatWorldcatRear,
  groupWsbCatch,
  heroWsbDiverKelpDark,
  diverBluefinBelow,
  diverYellowtailAscending,
} from '../assets/images/hero/imageAssets';

export interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  caption?: string;
  permalink?: string;
  category: 'underwater' | 'catch' | 'vessel' | 'crew';
}

const FALLBACK_IMAGES: GalleryImage[] = [
  {
    id: 'fb-01',
    src: heroUnderwaterBluefin,
    alt: 'Diver with Bluefin Tuna underwater',
    caption: 'Bluefin Tuna — crystal blue water',
    category: 'underwater',
  },
  {
    id: 'fb-02',
    src: heroBluefinDeck,
    alt: 'Bluefin Tuna catch on deck',
    caption: 'Bluefin Tuna on deck',
    category: 'catch',
  },
  {
    id: 'fb-03',
    src: heroChristosGrouper,
    alt: 'Captain Christos with giant Grouper',
    caption: 'Captain Christos — giant Grouper',
    category: 'catch',
  },
  {
    id: 'fb-04',
    src: heroWsbKelp,
    alt: 'White Seabass in kelp forest',
    caption: 'White Seabass in the kelp',
    category: 'underwater',
  },
  {
    id: 'fb-05',
    src: heroBluefinCloseup,
    alt: 'Bluefin Tuna closeup underwater',
    caption: 'Bluefin Tuna closeup',
    category: 'underwater',
  },
  {
    id: 'fb-06',
    src: captainChristosYellowtail,
    alt: 'Captain Christos with Yellowtail haul',
    caption: 'Captain Christos — Yellowtail haul',
    category: 'crew',
  },
  {
    id: 'fb-07',
    src: diverWsbUnderwater,
    alt: 'Diver with White Seabass underwater',
    caption: 'White Seabass underwater',
    category: 'underwater',
  },
  {
    id: 'fb-08',
    src: boatWorldcatAerial,
    alt: 'WorldCat 295CC aerial drone shot',
    caption: 'WorldCat 295CC from above',
    category: 'vessel',
  },
  {
    id: 'fb-09',
    src: boatWorldcatRear,
    alt: 'WorldCat 295CC rear view',
    caption: 'WorldCat 295CC — twin Suzukis',
    category: 'vessel',
  },
  {
    id: 'fb-10',
    src: groupWsbCatch,
    alt: 'Group catch — White Seabass',
    caption: 'Group catch — White Seabass',
    category: 'crew',
  },
  {
    id: 'fb-11',
    src: heroWsbDiverKelpDark,
    alt: 'Diver with WSB in dark kelp forest',
    caption: 'Dark kelp forest hunt',
    category: 'underwater',
  },
  {
    id: 'fb-12',
    src: diverBluefinBelow,
    alt: 'Diver with Bluefin Tuna from below',
    caption: 'Bluefin from below',
    category: 'underwater',
  },
  {
    id: 'fb-13',
    src: diverYellowtailAscending,
    alt: 'Diver ascending with Yellowtail',
    caption: 'Ascending with Yellowtail',
    category: 'underwater',
  },
];

interface InstagramAPIItem {
  id: string;
  media_url: string;
  caption?: string;
  permalink?: string;
  media_type: string;
}

interface UseInstagramFeedReturn {
  images: GalleryImage[];
  loading: boolean;
  error: string | null;
  isLive: boolean;
}

function mapCategory(caption: string | undefined): GalleryImage['category'] {
  if (!caption) return 'underwater';
  const lower = caption.toLowerCase();
  if (lower.includes('boat') || lower.includes('vessel') || lower.includes('worldcat')) return 'vessel';
  if (lower.includes('crew') || lower.includes('team') || lower.includes('group')) return 'crew';
  if (lower.includes('catch') || lower.includes('deck') || lower.includes('haul')) return 'catch';
  return 'underwater';
}

export function useInstagramFeed(): UseInstagramFeedReturn {
  const [images, setImages] = useState<GalleryImage[]>(FALLBACK_IMAGES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchFeed() {
      try {
        const response = await fetch('/api/v1/instagram/feed');

        if (!response.ok) {
          throw new Error(`API responded with ${response.status}`);
        }

        const data: { data: InstagramAPIItem[] } = await response.json();

        if (cancelled) return;

        const liveImages: GalleryImage[] = data.data
          .filter((item) => item.media_type === 'IMAGE' || item.media_type === 'CAROUSEL_ALBUM')
          .map((item) => ({
            id: item.id,
            src: item.media_url,
            alt: item.caption?.slice(0, 100) || 'Fussaki Spearfishing',
            caption: item.caption?.slice(0, 120),
            permalink: item.permalink,
            category: mapCategory(item.caption),
          }));

        if (liveImages.length > 0) {
          setImages(liveImages);
          setIsLive(true);
        }
        // If API returns empty, keep fallback
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load Instagram feed');
        // Keep fallback images — no state change needed
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchFeed();

    return () => {
      cancelled = true;
    };
  }, []);

  return { images, loading, error, isLive };
}

export default useInstagramFeed;
